import { gql } from '@apollo/client';
import { EntryFragment } from '../fragments/entry';

/**
 * Separate query for fetching rich text body with full links for Offer items
 * This is split out to avoid "query too long" errors
 */
export const MEETING_PLACE_PAGE_OFFER_ITEM_RICHTEXT_QUERY = gql`
  ${EntryFragment}
  query MeetingPlacePageOfferItemRichTextQuery(
    $ids: [String!]!
    $locale: String!
    $preview: Boolean
    $limit: Int
    $skip: Int
  ) {
    meetingPlacePageOfferCollection(
      where: { sys: { id_in: $ids } }
      locale: $locale
      preview: $preview
      limit: $limit
      skip: $skip
    ) {
      total
      items {
        sys {
          id
        }
        body {
          __typename
          json
          links {
            assets {
              __typename
              block {
                sys {
                  id
                }
                url
                width
                height
                __typename
              }
              hyperlink {
                sys {
                  id
                  locale
                }
                height
                width
                url
              }
            }
            entries {
              hyperlink {
                sys {
                  id
                  locale
                }
                __typename
                ...Entry
              }
              block {
                sys {
                  id
                }
                ...Entry
                __typename
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * Fetches rich text body data for MeetingPlacePageOffer items and merges it into the offer data
 * Supports both single offer items and arrays of offer items
 */
export async function fetchAndMergeOfferItemRichText(
  client: any,
  offerItems: any | any[],
  locale: string,
  preview?: boolean,
): Promise<any | any[]> {
  // Normalize to array for processing
  const isArray = Array.isArray(offerItems);
  const items = isArray ? offerItems : [offerItems];

  // Extract offer IDs
  const offerIds = items
    .filter((item: any) => item?.sys?.id || item?.id)
    .map((item: any) => item.sys?.id || item.id);

  if (offerIds.length === 0) {
    return offerItems;
  }

  try {
    // Helper function to fetch paginated rich text data for offer items
    const fetchPaginatedOfferRichText = async (
      ids: string[],
    ): Promise<Map<string, any>> => {
      const richTextMap = new Map<string, any>();
      const limit = 4; // Contentful's recommended limit

      // If IDs array is very large, split into chunks to avoid query size limits
      const chunkSize = 50; // Process 50 IDs at a time to avoid query size limits
      const idChunks: string[][] = [];

      for (let i = 0; i < ids.length; i += chunkSize) {
        idChunks.push(ids.slice(i, i + chunkSize));
      }

      // Process each chunk and accumulate all items
      for (const idChunk of idChunks) {
        let skip = 0;
        let total = 0;
        const chunkItems: any[] = [];

        // Fetch all pages for this chunk
        do {
          const res = await client.query({
            query: MEETING_PLACE_PAGE_OFFER_ITEM_RICHTEXT_QUERY,
            variables: {
              ids: idChunk,
              locale,
              preview,
              limit,
              skip,
            },
            fetchPolicy: 'no-cache',
            errorPolicy: 'all',
          });

          const collection = res?.data?.meetingPlacePageOfferCollection;
          if (!collection) break;

          const items = collection.items || [];
          chunkItems.push(...items);
          total = collection.total || 0;
          skip += limit;
        } while (chunkItems.length < total && skip < total);

        // Map chunk items to the richTextMap
        chunkItems.forEach((item: any) => {
          if (item.sys?.id && item.body) {
            richTextMap.set(item.sys.id, item.body);
          }
        });
      }

      return richTextMap;
    };

    // Fetch rich text data with pagination
    const richTextDataMap = await fetchPaginatedOfferRichText(offerIds);

    // Merge rich text data into offer items
    const mergedItems = items.map((item: any) => {
      if (!item) return item;

      const merged = { ...item };
      const offerId = merged.sys?.id || merged.id;

      if (offerId) {
        const richTextBody = richTextDataMap.get(offerId);
        if (richTextBody) {
          merged.body = richTextBody;
        }
      }

      return merged;
    });

    // Return in the same format as input (single item or array)
    return isArray ? mergedItems : mergedItems[0];
  } catch (error) {
    console.error('Error fetching offer item rich text body:', error);
    // Return original offer items if fetch fails
    return offerItems;
  }
}
